import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  HttpTransportType,
  LogLevel,
} from "@microsoft/signalr";
import * as url from "../api/urls";
import { useAuth } from "./AuthContext";

interface ISocketContext {
  connection: HubConnection | null;
  subscribeEvent: <T>(
    eventName: string,
    callback: (data: T) => void
  ) => () => void;
  disconnect: () => Promise<void>;
}

const SocketContext = createContext<ISocketContext | undefined>(undefined);

export const SocketProvider: React.FC<any> = ({ children }) => {

  const { user } = useAuth();

  const [connection, setConnection] = useState<HubConnection | null>(null);

  const hubRef = useRef<HubConnection | null>(null);

  const handlersRef = useRef<
    Map<string, Set<(data: any) => void>>
  >(new Map());

  // 🚀 CONEXÃO
  useEffect(() => {

    const token = user?.tokenJWT;

    // 🔴 LOGOUT
    if (!token) {

      if (hubRef.current) {

        console.log('[Socket] Logout → stop()');

        const hub = hubRef.current;

        hubRef.current = null;
        setConnection(null);

        hub.stop()
          .then(() => console.log('🔌 SignalR STOPPED'))
          .catch(err => console.error('SignalR stop error', err));
      }

      return;
    }

    // 🟡 já existe → não cria outro
    if (hubRef.current) return;

    console.log('[Socket] Criando conexão autenticada...');

    const hub = new HubConnectionBuilder()
      .withUrl(url.API_SOCKET, {
        accessTokenFactory: () => token,
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    hub.onreconnecting(() => {
      console.warn("🟡 SignalR RECONNECTING...");
    });

    hub.onreconnected(() => {

      console.log("🟢 SignalR RECONNECTED!");

      handlersRef.current.forEach((callbacks, eventName) => {
        callbacks.forEach(cb => {
          hub.on(eventName, cb);
        });
      });

    });

    hub.onclose(() => {
      console.error("🔴 SignalR CLOSED!");
    });

    hubRef.current = hub;
    setConnection(hub);

    const start = async () => {

      try {

        await hub.start();

        console.log("🟢 SignalR CONNECTED (JWT)!");

        // 🔥 reinstala eventos
        handlersRef.current.forEach((callbacks, eventName) => {
          callbacks.forEach(cb => {
            hub.on(eventName, cb);
          });
        });

      } catch (err) {

        console.error("🔴 SignalR ERROR:", err);

        hubRef.current = null;
        setConnection(null);
      }
    };

    start();

    return () => {

      if (hubRef.current === hub) {

        console.log('[Socket] cleanup → stop()');

        hubRef.current = null;
        setConnection(null);

        hub.stop()
          .then(() => console.log('🔌 SignalR STOPPED'))
          .catch(err => console.error('SignalR stop error', err));
      }
    };

  }, [user?.tokenJWT]);

  // 📡 SUBSCRIBE SAFE
  const subscribeEvent = <T,>(
    eventName: string,
    callback: (data: T) => void
  ): (() => void) => {

    if (!handlersRef.current.has(eventName)) {
      handlersRef.current.set(eventName, new Set());
    }

    handlersRef.current.get(eventName)!.add(callback);

    if (hubRef.current) {
      hubRef.current.on(eventName, callback);
    }

    return () => {
      handlersRef.current.get(eventName)?.delete(callback);
      hubRef.current?.off(eventName, callback);
    };
  };

  const disconnect = useCallback(async (): Promise<void> => {

    if (!hubRef.current) return;

    await hubRef.current.stop();

    hubRef.current = null;
    setConnection(null);

  }, []);

  return (
    <SocketContext.Provider value={{ connection, subscribeEvent, disconnect }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): ISocketContext => {

  const context = useContext(SocketContext);

  if (!context) {
    throw new Error("useSocket deve ser usado dentro de SocketProvider");
  }

  return context;
};
